import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core';
import {AppMainComponent} from './app.main.component';
import {AuthService} from "./core/service/auth.service";
import {Router} from "@angular/router";
import {User} from "./core/model/User";
import { HttpClient } from "@angular/common/http";
import {MessageService} from "primeng/api";
import {interval, Subscription} from "rxjs";
import {url_routes} from "./core/core.routes";
import {environment} from "../environments/environment";
import {SearchService} from "./core/service/search.service";
import {SearchRecord} from "./core/model/search/SearchRecord";
import {ConfigService} from "./app.config.service";

@Component({
    selector: 'app-topbar',
    templateUrl: './app.topbar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppTopBarComponent implements OnInit, OnDestroy {
    appMain = inject(AppMainComponent);
    private authService = inject(AuthService);
    private router = inject(Router);
    private http = inject(HttpClient);
    private service = inject(MessageService);
    private searchService = inject(SearchService);
    private configService = inject(ConfigService);


    user: User;
    can_login = 0;
    last_vm = '';
    subscription: Subscription;
    private configSub: Subscription;

    searchText: string;
    searchResponse: SearchRecord[];

    displayChangePassword = false;
    changingPassword = false;
    oldPassword = '';
    newPassword = '';
    confirmPassword = '';

    constructor() {
        this.authService.getObservableUser().subscribe(u => this.user = u);
    }

    logout() {
        this.authService.logout().subscribe(result => {
            this.router.navigateByUrl('/login')
        });
    }

    ngOnInit(): void {
        if (this.isSemaforVisible()) {
            this.refreshLogin(false);
            // Start/stop the 10s semafor poll based on the Polling setting, and react live
            // when it is toggled in the config panel.
            this.applyPolling(this.configService.config.pollingDisabled);
            this.configSub = this.configService.configUpdate$.subscribe(c => this.applyPolling(c.pollingDisabled));
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.configSub?.unsubscribe();
    }

    private applyPolling(disabled: boolean): void {
        this.subscription?.unsubscribe();
        if (!disabled) {
            this.subscription = interval(10000).subscribe(() => this.refreshLogin(false));
        }
    }

    isSemaforVisible() {
        return environment.production && location.hostname !== 'aos.zitrochu.sk';
    }

    refreshLogin(withToast = true): void {
        this.http.get('https://www.zitrochu.sk/automation/get_last.php').toPromise().then(r => {
            this.last_vm = String(r);
        });
        this.http.get('https://www.zitrochu.sk/automation/can_login.php').toPromise().then(r => {
            this.can_login = Number(r);
            if (withToast) {
                this.service.add({
                    key: 'topbar',
                    severity: 'success',
                    summary: 'Request o update bol úspešný',
                    detail: 'Podľa farby semafóru sa možeš lognuť'
                });
            }
        }).catch(e => {
            if (withToast) {
                this.service.add({
                    key: 'topbar',
                    severity: 'error',
                    summary: 'Request o stave semafóru nebol úspešný',
                    detail: e
                });
            }
        })
    }

    isPollingDisabled(): boolean {
        return !!this.configService.config.pollingDisabled;
    }

    togglePolling(): void {
        // Persisted in localStorage + emitted on configUpdate$, which applyPolling() (subscribed
        // in ngOnInit) picks up to stop/start the 10s semafor poll immediately.
        this.configService.updateConfig({
            ...this.configService.config,
            pollingDisabled: !this.configService.config.pollingDisabled
        });
    }

    resetCanLogin(): void {
        this.http.get('https://www.zitrochu.sk/automation/update_login.php?can_login=1').toPromise().then(r => {
            this.can_login = 1;
            this.service.add({
                key: 'topbar',
                severity: 'success',
                summary: 'Request o reset bol úspešný',
                detail: 'Semafór bol resetnutý'
            });
        }).catch(e => {
            this.service.add({
                key: 'topbar',
                severity: 'error',
                summary: 'Request o reset semafóru nebol úspešný',
                detail: e
            });
        })

    }

    resetStrategies() {
        this.http.get(url_routes.RESET_STRATEGIES).toPromise().then(r => {
            console.log(r);
        });
    }

    search() {
        if (this.searchText && this.searchText.trim().length >= 3) {
            this.searchService.search(this.searchText.trim()).then(r => this.searchResponse = r);
        }
    }

    navigateToFirstSearchRecord() {
        if (this.searchResponse != null && this.searchResponse.length > 0) {
            this.router.navigateByUrl(this.searchResponse[0].url);
        }
    }

    openChangePasswordDialog(): void {
        this.resetChangePassword();
        this.displayChangePassword = true;
    }

    resetChangePassword(): void {
        this.changingPassword = false;
        this.oldPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
    }

    submitChangePassword(): void {
        if (this.changingPassword) {
            return;
        }

        if (!this.oldPassword || !this.newPassword || !this.confirmPassword) {
            this.service.add({
                key: 'topbar',
                severity: 'warn',
                summary: 'Chyba',
                detail: 'Všetky polia musia byť vyplnené'
            });
            return;
        }

        if (this.newPassword === this.oldPassword) {
            this.service.add({
                key: 'topbar',
                severity: 'warn',
                summary: 'Chyba',
                detail: 'Nové heslo sa musí líšiť od starého'
            });
            return;
        }

        if (this.newPassword !== this.confirmPassword) {
            this.service.add({
                key: 'topbar',
                severity: 'warn',
                summary: 'Chyba',
                detail: 'Nové heslo a potvrdenie sa nezhodujú'
            });
            return;
        }

        const passwordChange = {
            oldPassword: this.oldPassword,
            newPassword: this.newPassword
        };

        this.changingPassword = true;
        // Must target the API host (environment.API_ENDPOINT), which in production is a different
        // origin than the SPA — a hardcoded relative '/aosdb/api/...' would hit the static SPA host
        // and 404, surfacing as the generic error toast below.
        this.http.post(environment.API_ENDPOINT + '/auth/changePassword', passwordChange).subscribe({
            next: () => {
                this.changingPassword = false;
                this.service.add({
                    key: 'topbar',
                    severity: 'success',
                    summary: 'Úspech',
                    detail: 'Heslo bolo úspešne zmenené'
                });
                this.displayChangePassword = false;
            },
            error: (error) => {
                this.changingPassword = false;
                // 401 from the backend means the supplied old password did not match.
                const detail = error?.status === 401
                    ? 'Staré heslo je nesprávne'
                    : 'Pri zmene hesla sa vyskytla chyba';
                this.service.add({
                    key: 'topbar',
                    severity: 'error',
                    summary: 'Chyba',
                    detail: detail
                });
            }
        });
    }
}
